import { useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { FaqSection } from "@/components/landing/Faq";
import { AREAS, SUB_DETAILS, FAQS, RELATED_ARTICLES } from "@/content/site";
import { ARTICLES_BY_SLUG } from "@/content/approfondimenti";

function ParallaxImage({ src, alt, className = "", gray = false }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y }} className={`absolute inset-0 w-full h-[116%] object-cover ${gray ? "grayscale-[20%]" : ""}`} />
      <div className="absolute inset-0 ring-1 ring-inset ring-current/10" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
      <p className="font-serif text-5xl">Pagina non trovata</p>
      <Link to="/" className="link-underline text-sm">Torna alla home</Link>
    </div>
  );
}

export function AreaPage() {
  const { area } = useParams();
  const navigate = useNavigate();
  const data = AREAS[area];
  if (!data) return <NotFound />;
  const dark = data.dark;
  const areaArticles = (RELATED_ARTICLES[area] || []).map((s) => ARTICLES_BY_SLUG[s]).filter(Boolean).slice(0, 3);

  return (
    <div className={dark ? "bg-graphite text-canvas" : "bg-canvas text-ink"}>
      {/* Hero */}
      <section data-testid="area-hero" className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-60 hover:opacity-100 link-underline mb-10">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <Reveal><Overline className={dark ? "text-canvas/70" : ""}>{data.overline}</Overline></Reveal>
              <Reveal delay={0.1} as="h1" className="mt-7 font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
                {data.h1 || data.hero}
              </Reveal>
              {data.h1 && (
                <Reveal delay={0.16} as="p" className={`mt-5 font-serif text-2xl md:text-3xl font-light leading-snug ${dark ? "text-canvas/70" : "text-muted"}`}>{data.hero}</Reveal>
              )}
            </div>
            <div className="lg:col-span-5">
              {data.intro.map((p, i) => (
                <Reveal key={i} delay={0.15 + i * 0.08} as="p" className={`text-lg leading-relaxed ${i > 0 ? "mt-4" : ""} ${dark ? "text-canvas/60" : "text-muted"}`}>{p}</Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <ParallaxImage src={data.image} alt={`${data.label} a Genova · Studio del Dott. Alessandro Valle`} className="h-[40vh] md:h-[62vh] w-full" gray={dark} />
        </div>
      </section>

      {/* Lists */}
      {data.lists && (
        <section className="pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {data.lists.map((l, li) => (
              <div key={li}>
                <Reveal as="p" className={`text-[11px] uppercase tracking-[0.2em] mb-6 ${dark ? "text-canvas/50" : "text-muted"}`}>{l.heading}</Reveal>
                <div className={l.numbered ? "" : "flex flex-wrap gap-2.5"}>
                  {l.items.map((it, i) =>
                    l.numbered ? (
                      <Reveal key={it} delay={i * 0.03} className={`flex items-center gap-3 border-t py-3 text-[15px] ${dark ? "border-canvas/15 text-canvas/80" : "border-ink/10 text-ink/80"}`}>
                        <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>{it}
                      </Reveal>
                    ) : (
                      <Reveal key={it} delay={i * 0.02} className={`border px-4 py-2 text-[13px] transition-colors duration-300 ${dark ? "border-canvas/25 text-canvas/70 hover:border-canvas" : "border-ink/15 text-ink/70 hover:border-sage hover:text-ink"}`}>{it}</Reveal>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Quote */}
      {data.quote && (
        <section className="pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal as="p" className="font-serif italic text-2xl md:text-3xl leading-snug max-w-3xl border-l-2 border-sage pl-8">
              {data.quote}
            </Reveal>
          </div>
        </section>
      )}

      {/* Machines */}
      {data.machines && (
        <section className="pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal><Overline className={dark ? "text-canvas/70" : ""}>Le nostre piattaforme</Overline></Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-current/10 border border-current/10">
              {data.machines.map((m, i) => (
                <Reveal key={m.name} delay={i * 0.08} className={`${dark ? "bg-graphite" : "bg-sand"} p-6 md:p-8`}>
                  <div className="w-full bg-white ring-1 ring-inset ring-ink/10 flex items-center justify-center p-4 h-56">
                    <img src={m.img} alt={`${m.brand} ${m.name} · ${data.label} a Genova`} className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-sagedark">{m.brand} · {m.spec}</p>
                  <h3 className="mt-2 font-serif text-3xl">{m.name}</h3>
                  <p className={`mt-4 text-[15px] leading-relaxed ${dark ? "text-canvas/60" : "text-muted"}`}>{m.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tech cards */}
      {data.cards && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
              {data.cards.map((c, i) => (
                <Reveal key={c.title} delay={(i % 3) * 0.05} className="bg-canvas p-8 group hover:bg-sand transition-colors duration-500">
                  <span className="font-serif text-sage text-xl">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 font-serif text-2xl leading-tight">{c.title}</h3>
                  <p className="mt-3 text-sm text-muted leading-relaxed">{c.body}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Subpages */}
      {data.subs && data.subs.length > 0 && (
        <section className={`py-20 md:py-28 ${dark ? "bg-canvas text-ink" : "bg-sand"}`}>
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal><Overline>Approfondimenti</Overline></Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
              {data.subs.map((s, i) => (
                <Reveal key={s.slug} delay={(i % 3) * 0.05}>
                  <Link to={`/${area}/${s.slug}`} data-testid={`sub-card-${s.slug}`} className="block h-full bg-canvas p-8 group hover:bg-stone transition-colors duration-500">
                    <div className="flex items-start justify-between">
                      <span className="font-serif text-sage text-xl">{String(i + 1).padStart(2, "0")}</span>
                      <ArrowUpRight className="w-5 h-5 text-ink/30 group-hover:text-ink transition-colors duration-300" />
                    </div>
                    <h3 className="mt-5 font-serif text-2xl leading-tight">{s.title}</h3>
                    <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-3">{s.body}</p>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <FaqSection items={FAQS[area]} dark={dark} title={`Domande frequenti · ${data.label} a Genova`} />

      {/* Approfondimenti correlati */}
      {areaArticles.length > 0 && (
        <section className={`py-16 md:py-24 ${dark ? "bg-canvas text-ink" : "bg-canvas"}`} data-testid="area-related-articles">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Reveal><Overline>{areaArticles.length === 1 ? "Vuoi approfondire?" : "Approfondimenti correlati"}</Overline></Reveal>
            <div className={`mt-10 grid grid-cols-1 gap-px bg-ink/10 border border-ink/10 ${areaArticles.length === 2 ? "md:grid-cols-2" : areaArticles.length >= 3 ? "md:grid-cols-2 lg:grid-cols-3" : ""}`}>
              {areaArticles.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.05}>
                  <Link to={`/approfondimenti/${a.slug}`} data-testid={`related-article-${a.slug}`} className="group block h-full bg-sand hover:bg-stone transition-colors duration-500">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={a.cover} alt={a.coverAlt || a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                    </div>
                    <div className="p-7">
                      <h3 className="font-serif text-xl leading-tight">{a.title}</h3>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm text-ink group-hover:gap-3 transition-all duration-300">Leggi l'approfondimento <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className={`py-20 md:py-28 ${dark ? "bg-graphite" : "bg-canvas"}`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
          <Reveal as="h2" className="font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight max-w-3xl mx-auto">
            Il trattamento corretto nasce da una valutazione corretta.
          </Reveal>
          <Reveal delay={0.1} as="div" className="mt-9">
            <button onClick={() => navigate("/contatti")} data-testid="area-cta" className="group relative inline-flex items-center overflow-hidden border border-current px-10 py-4 text-xs uppercase tracking-[0.2em]">
              <span className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${dark ? "bg-canvas" : "bg-ink"}`} />
              <span className={`relative transition-colors duration-500 ${dark ? "group-hover:text-graphite" : "group-hover:text-canvas"}`}>Prenota una valutazione</span>
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export function SubPage() {
  const { area, sub } = useParams();
  const navigate = useNavigate();
  const data = AREAS[area];
  const item = data?.subs?.find((s) => s.slug === sub);
  if (!data || !item) return <NotFound />;
  const others = data.subs.filter((s) => s.slug !== sub);
  const details = SUB_DETAILS[`${area}/${sub}`] || {};

  const HOW = [
    { t: "Valutazione", d: "Analisi della condizione, della storia clinica e delle aspettative." },
    { t: "Selezione della tecnologia", d: "Scelta della tecnologia o del trattamento più coerente con il bersaglio." },
    { t: "Personalizzazione", d: "Parametri, tecnica, numero di sedute e intervalli adattati alla persona." },
  ];
  const EXPECT = details.expect || ["Spesso più sedute distanziate nel tempo", "Miglioramento progressivo, non immediato", "Tempi di recupero variabili in base all’intensità", "Controllo della risposta e follow-up"];

  const relatedSlugs = (RELATED_ARTICLES[`${area}/${sub}`] || RELATED_ARTICLES[area] || []).slice(0, 3);
  const relatedArticles = relatedSlugs.map((s) => ARTICLES_BY_SLUG[s]).filter(Boolean);

  return (
    <div className="bg-canvas text-ink">
      <section className="pt-36 md:pt-44 pb-14">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-2 text-sm text-muted mb-10">
              <Link to="/" className="hover:text-ink link-underline">Home</Link>
              <span>/</span>
              <Link to={`/${area}`} className="hover:text-ink link-underline">{data.label}</Link>
            </div>
          </Reveal>
          <Reveal><Overline>{data.label}</Overline></Reveal>
          <Reveal delay={0.1} as="h1" className="mt-6 font-serif font-light text-5xl md:text-6xl leading-[1] tracking-tight">{item.h1 || item.title}</Reveal>
          <Reveal delay={0.2} as="p" className="mt-8 text-xl text-muted leading-relaxed">{item.body}</Reveal>
          {details.long && (
            <Reveal delay={0.26} as="p" className="mt-6 text-lg text-muted leading-relaxed">{details.long}</Reveal>
          )}
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-6">Come lavoriamo</p>
            {HOW.map((h, i) => (
              <Reveal key={h.t} delay={i * 0.05} className="border-t border-ink/10 py-4">
                <div className="flex items-baseline gap-3">
                  <span className="text-sage font-serif text-sm">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <p className="font-serif text-lg">{h.t}</p>
                    <p className="mt-1 text-sm text-muted leading-relaxed">{h.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-6">Cosa aspettarsi</p>
            {EXPECT.map((e, i) => (
              <Reveal key={e} delay={i * 0.05} className="flex items-start gap-3 border-t border-ink/10 py-4 text-[15px] text-ink/80">
                <span className="text-sage mt-1">—</span>{e}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <div className="bg-sand border-l-2 border-sage p-6 md:p-8">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-3">Buono a sapersi</p>
            <p className="text-base text-ink/80 leading-relaxed">
              {details.note || "Ogni percorso viene definito dopo una valutazione medica: benefici, limiti, numero di sedute e tempi di risposta vengono spiegati con chiarezza prima di iniziare. I risultati possono variare in base alle caratteristiche individuali."}
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-5">
            <button onClick={() => navigate("/contatti")} data-testid="sub-cta" className="group relative inline-flex items-center overflow-hidden border border-ink px-9 py-4 text-xs uppercase tracking-[0.2em]">
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
            </button>
            <Link to={`/${area}`} className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-underline">
              <ArrowLeft className="w-4 h-4" /> Torna a {data.label}
            </Link>
          </div>
        </div>
      </section>

      <FaqSection items={FAQS[`${area}/${sub}`]} title={`Domande frequenti · ${item.title} a Genova`} />

      {relatedArticles.length > 0 && (
        <section className="py-16 md:py-24 bg-canvas" data-testid="related-articles">
          <div className="max-w-[1000px] mx-auto px-6 md:px-10">
            <Overline>{relatedArticles.length === 1 ? "Vuoi approfondire?" : "Approfondimenti correlati"}</Overline>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
              {relatedArticles.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.06}>
                  <Link to={`/approfondimenti/${a.slug}`} data-testid={`related-article-${a.slug}`} className="group block h-full bg-sand hover:bg-stone transition-colors duration-500">
                    <div className="relative overflow-hidden h-48">
                      <img src={a.cover} alt={a.coverAlt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-7">
                      <p className="text-[11px] uppercase tracking-[0.2em] text-sagedark">Approfondimento</p>
                      <h3 className="mt-3 font-serif text-xl leading-snug">{a.title}</h3>
                      <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-2">{a.metaDescription}</p>
                      <span className="mt-4 inline-flex items-center gap-2 text-sm text-ink group-hover:gap-3 transition-all duration-300">Leggi l'approfondimento <ArrowRight className="w-4 h-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {others.length > 0 && (
        <section className="py-16 md:py-24 bg-sand">
          <div className="max-w-[1000px] mx-auto px-6 md:px-10">
            <Overline>Altri approfondimenti</Overline>
            <div className="mt-8 divide-y divide-ink/10 border-y border-ink/10">
              {others.map((s) => (
                <Link key={s.slug} to={`/${area}/${s.slug}`} className="group flex items-center justify-between py-5 hover:px-3 transition-[padding] duration-300">
                  <span className="font-serif text-2xl md:text-3xl">{s.title}</span>
                  <ArrowRight className="w-5 h-5 text-ink/40 group-hover:text-ink group-hover:translate-x-1 transition-all duration-300" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
